import { useState } from "react"
import { useNavigate } from "react-router"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import MapGL from "@urbica/react-map-gl"
// @ts-ignore
import Draw from "@urbica/react-map-gl-draw"
import "mapbox-gl/dist/mapbox-gl.css"
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css"
import bbox from "@turf/bbox"
import { useSaleCreateStore } from "../../stores/useSaleCreateStore"
import { MAPBOX_TOKEN, mapStyles } from "../../../../shared/constants"
import { createSale, updateSale, publishSale } from "../../services/salesApi"

export const ReviewStep = () => {
  const navigate = useNavigate()
  const {
    saleData,
    setSubmitting,
    isSubmitting,
    isEditMode,
    editingSaleId,
    reset,
  } = useSaleCreateStore()
  const [mapViewport, setMapViewport] = useState({
    latitude: 39.8283,
    longitude: -98.5795,
    zoom: 4,
  })
  const [apiError, setApiError] = useState<string>("")

  const formatDateTime = (date: Date | string | undefined) => {
    if (!date) return "Not set"
    const dateObj = typeof date === "string" ? new Date(date) : date
    if (isNaN(dateObj.getTime())) return "Invalid date"
    return dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    })
  }

  // Calculate map viewport to fit boundary geometry
  const calculateBoundaryViewport = () => {
    if (!saleData.boundary_geom) {
      return mapViewport
    }

    try {
      const feature = {
        type: "Feature" as const,
        geometry: saleData.boundary_geom,
        properties: {},
      }

      const bounds = bbox(feature)
      const [minLng, minLat, maxLng, maxLat] = bounds

      // Calculate center point
      const centerLng = (minLng + maxLng) / 2
      const centerLat = (minLat + maxLat) / 2

      // Calculate zoom level based on bounds size
      const lngDiff = maxLng - minLng
      const latDiff = maxLat - minLat
      const maxDiff = Math.max(lngDiff, latDiff)

      let zoom = 10
      if (maxDiff > 1) zoom = 8
      else if (maxDiff > 0.1) zoom = 12
      else if (maxDiff > 0.01) zoom = 14
      else zoom = 16

      return {
        latitude: centerLat,
        longitude: centerLng,
        zoom,
      }
    } catch (error) {
      console.warn("Error calculating boundary viewport:", error)
      return mapViewport
    }
  }

  const handleSubmit = async (shouldPublish: boolean = false) => {
    setSubmitting(true)
    setApiError("") // Clear any previous errors

    try {
      let createdOrUpdatedSaleId: number | undefined

      if (isEditMode && editingSaleId) {
        // Update existing sale
        await updateSale(editingSaleId, saleData)
        createdOrUpdatedSaleId = editingSaleId

        // If user chose to publish after updating, publish the sale
        if (shouldPublish) {
          await publishSale(createdOrUpdatedSaleId)
        }
      } else {
        // Create new sale (with direct publishing if requested)
        const result = await createSale(saleData, shouldPublish)
        createdOrUpdatedSaleId = result.id

        // Clear the saved draft only for create mode
        localStorage.removeItem("sale-create-draft")
      }

      // Reset the store
      reset()

      // Navigate to the birddog dashboard
      navigate("/birddog")
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} sale:`,
        error
      )

      if (error instanceof Error) {
        setApiError(error.message)
      } else {
        setApiError(
          `An unexpected error occurred while ${
            isEditMode ? "updating" : "creating"
          } the sale.`
        )
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-8">
      {/* Review Section */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Sale Review</h3>
        <p className="text-gray-600 mb-6">
          Please review all details before creating your timber sale.
        </p>

        <div className="bg-white border rounded-lg divide-y">
          {/* Basic Information */}
          <div className="p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">
              Basic Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Name:</span>{" "}
                {saleData.name || "Not set"}
              </div>
              <div>
                <span className="font-medium">Location:</span>{" "}
                {saleData.location_text || "Not set"}
              </div>
              <div>
                <span className="font-medium">Sale Type:</span>{" "}
                {saleData.sale_type}
              </div>
              <div>
                <span className="font-medium">Description:</span>{" "}
                {saleData.description ? "Provided" : "Not provided"}
              </div>
            </div>
          </div>

          {/* Bidding Schedule */}
          <div className="p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">
              Bidding Schedule
            </h4>
            <div className="space-y-1 text-sm">
              <div>
                <span className="font-medium">Bidding Opens:</span>{" "}
                {formatDateTime(saleData.bid_start_datetime)}
              </div>
              <div>
                <span className="font-medium">Bidding Closes:</span>{" "}
                {formatDateTime(saleData.bid_stop_datetime)}
              </div>
              <div>
                <span className="font-medium">Bid Selection:</span>{" "}
                {formatDateTime(saleData.bid_select_datetime)}
              </div>
            </div>
          </div>

          {/* Boundaries */}
          <div className="p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-4">
              Boundaries
            </h4>
            {saleData.boundary_geom ? (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  ✓ Boundary defined on map
                </p>
                <div className="bg-gray-100 rounded-lg overflow-hidden">
                  <div className="h-80">
                    <MapGL
                      style={{ width: "100%", height: "100%" }}
                      mapStyle={mapStyles.aerial.url}
                      accessToken={MAPBOX_TOKEN}
                      {...calculateBoundaryViewport()}
                      onViewportChange={setMapViewport}
                    >
                      <Draw
                        data={{
                          type: "FeatureCollection",
                          features: [
                            {
                              type: "Feature",
                              geometry: saleData.boundary_geom,
                              properties: {},
                            },
                          ],
                        }}
                        styles={[
                          // Fill style
                          {
                            id: "boundary-fill",
                            type: "fill",
                            filter: ["==", "$type", "Polygon"],
                            paint: {
                              "fill-color": "#f97316",
                              "fill-opacity": 0.4,
                            },
                          },
                          // Line style
                          {
                            id: "boundary-line",
                            type: "line",
                            filter: ["==", "$type", "Polygon"],
                            paint: {
                              "line-color": "#ea580c",
                              "line-width": 3,
                            },
                          },
                        ]}
                        displayControlsDefault={false}
                        controls={{}}
                      />
                    </MapGL>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-600">⚠️ No boundary defined</p>
            )}
          </div>

          {/* Timber Products */}
          <div className="p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">
              Timber Products
            </h4>
            {saleData.timber_products && saleData.timber_products.length > 0 ? (
              <div className="text-sm">
                <p className="text-gray-600 mb-2">
                  {saleData.timber_products.length} products:
                </p>
                <ul className="space-y-1 mb-4">
                  {saleData.timber_products.map((product, index) => (
                    <li key={index} className="text-gray-700">
                      • {product.name}:{" "}
                      {product.estimated_volume.toLocaleString()} {product.unit}
                    </li>
                  ))}
                </ul>
                {saleData.inventory_description && (
                  <div>
                    <p className="text-gray-600 font-medium mb-1">
                      Inventory Description:
                    </p>
                    <p className="text-gray-700 text-sm whitespace-pre-wrap">
                      {saleData.inventory_description}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-600">
                ⚠️ No timber products defined
              </p>
            )}
          </div>

          {/* Terms */}
          <div className="p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-4">
              Terms & Conditions
            </h4>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Bidding Terms */}
              <div>
                <h5 className="text-sm font-medium text-gray-900 mb-2">
                  Bidding Terms
                </h5>
                {saleData.bidding_tc ? (
                  <div className="text-sm text-gray-900">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {saleData.bidding_tc}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    No bidding terms provided
                  </p>
                )}
              </div>

              {/* Contract Terms */}
              <div>
                <h5 className="text-sm font-medium text-gray-900 mb-2">
                  Contract Terms
                </h5>
                {saleData.contract_tc ? (
                  <div className="text-sm text-gray-900">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {saleData.contract_tc}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    No contract terms provided
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {apiError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error {isEditMode ? "Updating" : "Creating"} Sale
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <pre className="whitespace-pre-wrap font-sans">{apiError}</pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Submit Buttons */}
      <div className="flex justify-center space-x-4">
        <button
          onClick={() => void handleSubmit(false)}
          disabled={isSubmitting}
          className={`px-6 py-3 text-base font-medium rounded-md transition-colors ${
            !isSubmitting
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : "bg-gray-400 text-gray-600 cursor-not-allowed"
          }`}
        >
          {isSubmitting ? (
            <div className="flex items-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Saving...
            </div>
          ) : isEditMode ? (
            "Save Changes"
          ) : (
            "Save as Draft"
          )}
        </button>
        <button
          onClick={() => void handleSubmit(true)}
          disabled={isSubmitting}
          className={`px-6 py-3 text-base font-medium rounded-md transition-colors ${
            !isSubmitting
              ? "bg-green-600 text-white hover:bg-green-700"
              : "bg-gray-400 text-gray-600 cursor-not-allowed"
          }`}
        >
          {isSubmitting ? (
            <div className="flex items-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Publishing...
            </div>
          ) : isEditMode ? (
            "Save & Publish"
          ) : (
            "Publish Sale"
          )}
        </button>
      </div>
    </div>
  )
}
